import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import PageTitle from '../components/PageTitle';
import { API_URL } from '../config';
import { formatPhone } from '../utils/formatPhone';
import './PublicRegistrationPage.css';

const initialForm = {
  first_name: '',
  last_name: '',
  parent_name: '',
  parent_phone: '',
  previous_markaz: '',
  shift: '',
};

const PublicRegistrationPage = () => {
  const { markazSlug } = useParams();
  const [markaz, setMarkaz] = useState(null);
  const [shifts, setShifts] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    let isMounted = true;

    const loadRegistration = async () => {
      setLoading(true);
      setError('');

      try {
        const { data } = await axios.get(`${API_URL}/public-registration/${markazSlug}`);
        if (!isMounted) return;
        setMarkaz(data.markaz);
        setShifts(data.shifts || []);
        setForm((current) => ({ ...current, shift: data.shifts?.[0] || '' }));
      } catch (err) {
        if (!isMounted) return;
        setError(err?.response?.data?.error || 'Registration is not available right now.');
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    loadRegistration();

    return () => {
      isMounted = false;
    };
  }, [markazSlug]);

  const updateField = (field, value) => {
    setForm((current) => ({
      ...current,
      [field]: field === 'parent_phone' ? formatPhone(value) : value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setError('');
    setSuccess('');

    try {
      await axios.post(`${API_URL}/public-registration/${markazSlug}`, form);
      setSuccess('Registration submitted. The admin team will review it from the waiting list.');
      setForm({ ...initialForm, shift: shifts[0] || '' });
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to submit registration. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="public-register-page">
      <PageTitle
        title="Public Registration"
        description="Submit a MarkazSync student registration request for admin review."
        noIndex
      />

      <section className="public-register-shell">
        <div className="public-register-intro">
          <Link className="public-register-home" to="/">
            <i className="bi bi-arrow-left" aria-hidden="true" />
            Home
          </Link>
          <span className="public-register-pill">Public registration</span>
          <h1>{markaz ? `${markaz.name} Registration` : 'Student Registration'}</h1>
          <p>
            Submit student and parent details for review. Approved records can be managed from
            the admin waiting list.
          </p>
        </div>

        <div className="public-register-card">
          {loading ? (
            <div className="public-register-state">Loading registration form...</div>
          ) : error && !markaz ? (
            <div className="public-register-state public-register-state--error">{error}</div>
          ) : (
            <form onSubmit={handleSubmit} className="public-register-form">
              <div className="public-register-form__grid">
                <label>
                  <span>Student first name</span>
                  <input
                    type="text"
                    value={form.first_name}
                    onChange={(event) => updateField('first_name', event.target.value)}
                    required
                  />
                </label>

                <label>
                  <span>Student last name</span>
                  <input
                    type="text"
                    value={form.last_name}
                    onChange={(event) => updateField('last_name', event.target.value)}
                    required
                  />
                </label>

                <label>
                  <span>Parent name</span>
                  <input
                    type="text"
                    value={form.parent_name}
                    onChange={(event) => updateField('parent_name', event.target.value)}
                    required
                  />
                </label>

                <label>
                  <span>Parent phone</span>
                  <input
                    type="tel"
                    value={form.parent_phone}
                    onChange={(event) => updateField('parent_phone', event.target.value)}
                    required
                  />
                </label>

                <label>
                  <span>Previous markaz</span>
                  <input
                    type="text"
                    value={form.previous_markaz}
                    onChange={(event) => updateField('previous_markaz', event.target.value)}
                    placeholder="Optional"
                  />
                </label>

                <label>
                  <span>Preferred shift</span>
                  <select
                    value={form.shift}
                    onChange={(event) => updateField('shift', event.target.value)}
                    required
                  >
                    {shifts.map((shift) => (
                      <option key={shift} value={shift}>
                        {shift}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              {error && <div className="public-register-alert public-register-alert--error">{error}</div>}
              {success && <div className="public-register-alert public-register-alert--success">{success}</div>}

              <button type="submit" disabled={submitting}>
                {submitting ? 'Submitting...' : 'Submit Registration'}
              </button>

              <div className="public-register-legal">
                <Link to="/privacy">Privacy</Link>
                <Link to="/terms">Terms</Link>
              </div>
            </form>
          )}
        </div>
      </section>
    </main>
  );
};

export default PublicRegistrationPage;
